import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { submitCommunityTaskSolution, payForCommunityTaskAttempt } from '../services/api';

const CommunityTaskDetail = ({ task, onClose, onTaskCompleted }) => {
  const { user, updateUser } = useAuth();
  const [solution, setSolution] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);
  const [hasPaid, setHasPaid] = useState(false);
  const [showPaymentPrompt, setShowPaymentPrompt] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  // Check if user has already paid for this task and if they've completed it
  useEffect(() => {
    if (task && user) {
      // Check payment status
      if (task.paidAttempts) {
        const userPaid = task.paidAttempts.some(attempt => 
          attempt.userId === user._id
        );
        setHasPaid(userPaid);
      }
      
      // Check completion status
      if (task.completedBy) {
        const userCompleted = task.completedBy.includes(user._id);
        setHasCompleted(userCompleted);
      }
    }
  }, [task, user]);

  // Handle payment for task attempt
  const handlePayment = async () => {
    setPaying(true);
    setResult(null);
    
    try {
      const response = await payForCommunityTaskAttempt(task._id);
      
      if (response.success) {
        setHasPaid(true);
        setShowPaymentPrompt(false);
        setResult({
          success: true,
          message: response.message
        });
        
        // Update user's WR coins in context and localStorage
        if (updateUser) {
          await updateUser({ wrCoins: response.remainingCoins });
        }
        
        // Clear the result message after a few seconds so user can see the solution form
        setTimeout(() => {
          setResult(null);
        }, 3000);
      }
    } catch (err) {
      console.error('Error processing payment:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process payment. Please try again.';
      setResult({
        success: false,
        message: errorMessage
      });
    } finally {
      setPaying(false);
    }
  };

  // Handle solution submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!solution.trim()) {
      setResult({
        success: false,
        message: 'Please enter a solution first.'
      });
      return;
    }
    
    setSubmitting(true);
    setResult(null);
    
    try {
      // Submit the solution
      const response = await submitCommunityTaskSolution(task._id, solution);
      
      setResult(response);
      
      // If solution was correct
      if (response.success) {
        // Update user XP in localStorage for immediate UI update
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const userData = JSON.parse(userStr);
          userData.xp = response.newXp || (userData.xp + 10); // Default to +10 XP if server doesn't return newXp
          localStorage.setItem('user', JSON.stringify(userData));
        }
        
        // Update completion status if this was the first completion
        if (!response.alreadyCompleted) {
          setHasCompleted(true);
        }
        
        // Notify parent component of completion
        onTaskCompleted();
      }
    } catch (err) {
      console.error('Error submitting solution:', err);
      
      // Check if payment is required
      if (err.response?.status === 402) {
        setShowPaymentPrompt(true);
        setResult({
          success: false,
          message: err.response.data.message || 'Payment required to attempt this task.'
        });
      } else {
        setResult({
          success: false,
          message: err.response?.data?.message || err.message || 'Failed to submit solution. Please try again.'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  // Toggle hint visibility
  const toggleHint = () => {
    setShowHint(!showHint);
  };

  return (
    <div className="community-modal">
      <div className="community-modal-content">
        <button 
          className="community-modal-close" 
          onClick={onClose}
        >
          ✕
        </button>
        
        <h2 className="community-modal-title">{task.title}</h2>
        
        <div className="community-task-meta">
          <span className="community-task-type">{task.type}</span>
          <span className="community-task-creator">
            by {task.createdBy.username || 'Anonymous'}
          </span>
        </div>
        
        <div className="community-task-detail">
          <p>{task.description}</p>
          
          <div className="community-task-content">
            {task.content}
          </div>
          
          {task.hint && (
            <div className="community-hint-container">
              <button 
                className="community-hint-toggle" 
                onClick={toggleHint}
              >
                {showHint ? 'Hide Hint' : 'Show Hint'}
              </button>
              
              {showHint && (
                <div className="community-hint">
                  <div className="community-hint-title">Hint:</div>
                  <div className="community-hint-content">{task.hint}</div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Only show solution form if the user isn't the creator */}
        {task.createdBy._id !== user._id && (
          <div className="community-task-interaction">
            {/* Completion status */}
            {hasCompleted && (
              <div className="community-completion-status">
                <div className="completion-badge">
                  🏆 <strong>Task Completed!</strong>
                </div>
                <p>You have already successfully completed this task and earned 10 XP. Great work!</p>
                <div className="completion-note">
                  <small>Note: Each task can only be completed once per user for XP rewards.</small>
                </div>
              </div>
            )}

            {/* Payment status and cost info (only show if not completed) */}
            {!hasCompleted && (
              <div className="community-payment-info">
                <div className="community-cost-display">
                  <strong>Cost to attempt: 100 WR Coins</strong>
                  <div className="community-cost-breakdown">
                    <small>• 0.5 WR Coins go to creator ({task.createdBy.username})</small>
                    <small>• 0.5 WR Coins go to community pool</small>
                  </div>
                </div>
                
                {hasPaid ? (
                  <div className="community-payment-status paid">
                    ✅ Paid - You can now solve this task!
                  </div>
                ) : (
                  <div className="community-payment-status unpaid">
                    ⚠️ Payment required to submit solutions
                  </div>
                )}
              </div>
            )}

            {/* Payment prompt - only show if not completed */}
            {showPaymentPrompt && !hasPaid && !hasCompleted && (
              <div className="community-payment-prompt">
                <p>You need to pay 100 WR Coins to attempt this task.</p>
                <div className="community-payment-actions">
                  <button 
                    className="community-pay-button"
                    onClick={handlePayment}
                    disabled={paying || (user?.wrCoins || 0) < 100}
                  >
                    {paying ? 'Processing...' : `Pay 100 WR Coins (You have ${user?.wrCoins || 0})`}
                  </button>
                  <button 
                    className="community-cancel-button"
                    onClick={() => setShowPaymentPrompt(false)}
                    disabled={paying}
                  >
                    Cancel
                  </button>
                </div>
                {(user?.wrCoins || 0) < 100 && (
                  <p className="community-insufficient-funds">
                    Insufficient funds. You need at least 100 WR Coins.
                  </p>
                )}
              </div>
            )}

            {/* Pre-payment button for easy access - only show if not completed */}
            {!hasPaid && !showPaymentPrompt && !hasCompleted && (
              <div className="community-prepay-section">
                <button 
                  className="community-prepay-button"
                  onClick={handlePayment}
                  disabled={paying || (user?.wrCoins || 0) < 100}
                >
                  {paying ? 'Processing...' : `Pay 100 WR Coins to Attempt (You have ${user?.wrCoins || 0})`}
                </button>
                {(user?.wrCoins || 0) < 100 && (
                  <p className="community-insufficient-funds">
                    You need at least 100 WR Coins to attempt this task.
                  </p>
                )}
              </div>
            )}

            {/* Solution form - only show if paid or creator, and not completed (unless creator) */}
            {(hasPaid || task.createdBy._id === user._id) && (!hasCompleted || task.createdBy._id === user._id) && (
              <div className="community-solution-section">
                <div className="community-challenge-ready">
                  🎯 <strong>Challenge Ready!</strong> Read the task content above and submit your solution below.
                </div>
                <form className="community-solution-form" onSubmit={handleSubmit}>
                  <div className="community-form-group">
                    <label className="community-form-label">Your Solution:</label>
                    <textarea
                      className="community-solution-input"
                      placeholder="Enter your solution here..."
                      value={solution}
                      onChange={(e) => setSolution(e.target.value)}
                      required
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="community-submit-button"
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Solution'}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
        
        {/* Show result message if there is one */}
        {result && (
          <div className={`community-result ${result.success ? 'success' : 'error'}`}>
            <p>{result.message}</p>
            {/* No need for additional rewards message as it's included in the main message */}
          </div>
        )}
        
        {/* Show task stats and solution only if user is the creator */}
        {task.createdBy._id === user._id && (
          <div className="community-creator-view">
            <div className="community-form-group">
              <label className="community-form-label">Solution:</label>
              <div className="community-task-content">{task.solution}</div>
            </div>
            
            <div className="community-task-stats">
              <div>Attempts: <strong>{task.attempts || 0}</strong></div>
              <div>Completions: <strong>{task.completions || 0}</strong></div>
              <div>Success Rate: <strong>
                {task.attempts ? Math.round((task.completions / task.attempts) * 100) : 0}%
              </strong></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityTaskDetail;